import "./App.scss";
import { useState } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import Login from "./app/pages/auth/Login";
import ProtectedRoute from "./app/pages/auth/ProtectedRoute";
import Projects from "./app/pages/projects/Projects";
import ProjectLayout from "./app/ProjectLayout";
import ProjectHome from "./app/pages/home/ProjectHome";
import AuditTrail from "./app/pages/audit/AuditTrail";
import AiAssistant from "./app/pages/ai/AiAssistant";
import PlanningCalendar from "./app/pages/calendar/PlanningCalendar";
import Planning from "./app/pages/planning/Planning";
import SiteVisitSummaryPage from "./app/pages/siteVisitSummary/SiteVisitSummaryPage";
import Completed from "./app/pages/completed/Completed";
import Reporting from "./app/pages/reporting/Reporting";
import Implementation from "./app/pages/implementation/Implementation";
import MainLayout from "./app/MainLayout";
import UserManagement from "./app/pages/UserManagement/UserManagement";
import Workspaces from "./app/pages/workspace/Workspaces";
import NotFound from "./app/components/NotFound/NotFound";
import IpManagement from "./app/pages/IpManagement/TableView";
import SettingsLayout from "./app/SettingsLayout";
import LoginInformation from "./app/pages/accountSettings/LoginInformation/LoginInformation";
import ProfileInformation from "./app/pages/accountSettings/ProfileInformation/ProfileInformation";
import PaymentInformation from "./app/pages/accountSettings/Payments/PaymentInformation";
import FirstTimeSignIn from "./app/pages/firstTimeLogIn/firstTime";
import ConfirmationCode from "./app/pages/firstTimeLogIn/confirmationCode";
import ForgotPassword from "./app/pages/forgotPassword/forgotPassword";
import ForgotPasswordCode from "./app/pages/forgotPassword/code";
import NewPassword from "./app/pages/forgotPassword/NewPassword";
import MainFlags from "./app/pages/Flags/MainFlags";
import BudgetSummary from "./app/pages/BudgetSummary/BudgetSummary";
import Offices from "./app/pages/offices/Offices";
import { createSignalRContext } from "react-signalr/signalr";
import { HttpTransportType } from "@microsoft/signalr";
import { getRelativeTime } from "./services/helpFunctions/getRelativeTime";
import { RolesEnum } from "./services/rolesEnum";
import { isAuthorized } from "./services/authService";
import { getFreshAccessToken } from "./services/tokenRefresh";
import PublicMap from "./app/pages/publicMap/PublicMap";
import ShareRoutes from "./shareRoute";
import SignUp from "./app/pages/signup/Signup";
import Payment from "./app/pages/payment/Payment";
import PaymentSuccess from "./app/pages/payment/PaymentSuccess";
import PaymentFail from "./app/pages/payment/PaymentFail";

const SignalRContext = createSignalRContext();
const { REACT_APP_API_BASE_URL, REACT_APP_API_IS_GUEST } = process.env;
const isGuestShare = REACT_APP_API_IS_GUEST;

function App() {
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const url = `${REACT_APP_API_BASE_URL}notifications/`;
  const loggedUserId = localStorage.getItem("userID");

  SignalRContext.useSignalREffect("Notification", (data) => {
    const userMatch = data?.userIds?.includes(loggedUserId) || false;
    if (userMatch) {
      pushNewNotificationsData(data);
    }
  });

  const pushNewNotificationsData = (notificationData) => {
    if (notificationData) {
      const data = {
        id: notificationData?.id,
        type: notificationData?.notificationType,
        description: notificationData?.message,
        entityId: notificationData?.entityId,
        read: notificationData?.isRead,
        title: notificationData?.title,
        time: getRelativeTime(notificationData?.createdDate) || "",
        contractId: notificationData?.contractId,
        projectId: notificationData?.projectId,
      };

      setUnreadNotifications((prevUnread) => {
        return prevUnread + 1;
      });

      setNotifications((prevNotifications) => ({
        ...prevNotifications,
        data: [data, ...(prevNotifications?.data.slice(0, -1) || [])],
      }));
    }
  };

  const token = localStorage.getItem("token");

  return (
    <SignalRContext.Provider
      connectEnabled={true}
      dependencies={[token]}
      url={url}
      skipNegotiation={true}
      transport={HttpTransportType.WebSockets}
      // Read on every (re)connect, so a token refreshed after login is used
      // instead of the one present when the connection was first built.
      accessTokenFactory={getFreshAccessToken}
    >
      <div className="App">
        <Router>
          {isGuestShare ? (
            <ShareRoutes />
          ) : (
            <Routes>
              <Route path="/" element={<Navigate replace to="/login" />} />
              <Route path="/publicMap" element={<PublicMap />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgotPassword" element={<ForgotPassword />} />
              <Route path="/sign-up" element={<SignUp />} />
              <Route
                path="/forgotPassword/code"
                element={<ForgotPasswordCode />}
              />
              <Route path="/newPassword" element={<NewPassword />} />
              <Route path="confirmationCode" element={<ConfirmationCode />} />

              <Route path="/*" element={<ProtectedRoute />}>
                <Route path="payment" element={<Payment />} />
                <Route path="payment-success" element={<PaymentSuccess />} />
                <Route path="payment-failure" element={<PaymentFail />} />
                <Route
                  path="dashboard/*"
                  element={
                    <MainLayout
                      notifications={notifications}
                      setNotifications={setNotifications}
                      unreadNotifications={unreadNotifications}
                      setUnreadNotifications={setUnreadNotifications}
                    />
                  }
                >
                  <Route
                    path="user-management"
                    element={
                      isAuthorized([
                        RolesEnum.ProjectManager,
                        RolesEnum.SystemAdmin,
                        RolesEnum.FieldCoordinator,
                      ]) ? (
                        <UserManagement />
                      ) : (
                        <Navigate replace to="/dashboard/contracts" />
                      )
                    }
                  />
                  <Route path="contracts" element={<Workspaces />} />
                  <Route path="budget-summary" element={<BudgetSummary />} />
                  <Route path="offices" element={<Offices />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
                <Route
                  path="projects"
                  element={
                    <Projects
                      notifications={notifications}
                      setNotifications={setNotifications}
                      unreadNotifications={unreadNotifications}
                      setUnreadNotifications={setUnreadNotifications}
                    />
                  }
                />
                <Route path="first-time" element={<FirstTimeSignIn />} />
                <Route
                  path="project/*"
                  element={
                    <ProjectLayout
                      notifications={notifications}
                      setNotifications={setNotifications}
                      unreadNotifications={unreadNotifications}
                      setUnreadNotifications={setUnreadNotifications}
                    />
                  }
                >
                  <Route path="home" element={<ProjectHome />} />
                  <Route path="calendar" element={<PlanningCalendar />} />
                  {/* Dashboard merged into the project Home page. */}
                  <Route
                    path="dashboard"
                    element={<Navigate replace to="/project/home" />}
                  />
                  <Route path="planning" element={<Planning />} />
                  <Route
                    path="site-visit-summary"
                    element={<SiteVisitSummaryPage />}
                  />
                  <Route path="implementation" element={<Implementation />} />
                  <Route path="reporting" element={<Reporting />} />
                  <Route path="completed" element={<Completed />} />
                  <Route path="flags" element={<MainFlags />} />
                  <Route path="ip-management" element={<IpManagement />} />
                  <Route
                    path="audit"
                    element={
                      isAuthorized([
                        RolesEnum.ProjectManager,
                        RolesEnum.SystemAdmin,
                      ]) ? (
                        <AuditTrail />
                      ) : (
                        <Navigate replace to="home" />
                      )
                    }
                  />
                  <Route
                    path="ai"
                    element={
                      isAuthorized([
                        RolesEnum.ProjectManager,
                        RolesEnum.SystemAdmin,
                        RolesEnum.FieldCoordinator,
                      ]) ? (
                        <AiAssistant />
                      ) : (
                        <Navigate replace to="home" />
                      )
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Route>
                <Route
                  path="settings/*"
                  element={
                    <SettingsLayout
                      notifications={notifications}
                      setNotifications={setNotifications}
                      unreadNotifications={unreadNotifications}
                      setUnreadNotifications={setUnreadNotifications}
                    />
                  }
                >
                  <Route
                    path="login-information"
                    element={<LoginInformation />}
                  />
                  <Route path="payments" element={<PaymentInformation />} />
                  <Route
                    path="profile-information"
                    element={<ProfileInformation />}
                  />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          )}
        </Router>
      </div>
    </SignalRContext.Provider>
  );
}

export default App;
